import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useRentalsCustomerAuth } from 'src/auth/rentals-customer/rentals-customer-auth';
import rentalsCustomerAxios, { rentalsCustomerEndpoints } from 'src/utils/rentals-customer-axios';

type Branding = {
  companyName: string;
  companyLogoUrl: string;
};

export default function Page() {
  const { login } = useRentalsCustomerAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const organizationId = searchParams.get('org');

  const [branding, setBranding] = useState<Branding | null>(null);

  useEffect(() => {
    if (!organizationId || !Number(organizationId)) {
      return;
    }
    // branding is a nicety — on any failure the page just keeps the default look
    rentalsCustomerAxios
      .get(rentalsCustomerEndpoints.branding.replace(':organizationId', organizationId))
      .then((response) => setBranding(response.data))
      .catch(() => {});
  }, [organizationId]);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const submit = () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage('');
    login(username, password)
      .then(() => {
        router.push(paths.rentalsPortal.root);
      })
      .catch((error) => {
        if (error?.response?.status === 422 && error.response.data?.length) {
          setErrorMessage(error.response.data[0].message);
        } else {
          enqueueSnackbar('An unexpected error has occured. Please try again.', {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      submit();
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ bgcolor: 'grey.100', p: 2 }}
    >
      <Card sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 420, borderRadius: 2 }}>
        <Stack gap={2.5}>
          <Stack alignItems="center" gap={1.5} textAlign="center">
            <Logo dark customLogoUrl={branding?.companyLogoUrl || undefined} sx={{ justifyContent: 'center' }} />
            <Box>
              <Typography variant="h5" sx={{ textWrap: 'balance' }}>
                {branding?.companyName || 'Trailer Rentals'}
              </Typography>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: '0.12em' }}
              >
                Customer Portal
              </Typography>
            </Box>
          </Stack>

          {errorMessage && (
            <Typography color="error" variant="body2" textAlign="center">
              {errorMessage}
            </Typography>
          )}

          <TextField
            label="Username"
            size="medium"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={onKeyDown}
          />

          <TextField
            label="Password"
            size="medium"
            type={passwordVisible ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge="end">
                    <Iconify icon={passwordVisible ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button variant="contained" color="primary" size="large" onClick={submit} disabled={submitting}>
            {submitting && <CircularProgress size={18} sx={{ mr: 1 }} />}
            Log In
          </Button>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            Your login details were provided by the rental company
          </Typography>
        </Stack>
      </Card>

      <Typography variant="caption" color="text.disabled" pt={2}>
        Powered by Counting Trucks
      </Typography>
    </Box>
  );
}
